import { Badge } from "@circleco/compass/components/Badge";
import { Popover } from "@circleco/compass/components/Popover";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import { useCommentsContainer } from "@/react/components/CommentsCompass/CommentsContainer";
import {
  filterVisibleTags,
  isAuthorAiAgent,
  isAuthorAdmin,
  isReply,
} from "@/react/components/CommentsCompass/helpers";
import { cssAnchorProps } from "@/react/helpers/cssAnchorHelpers";
import type { Comment } from "@/react/types/Comment";

import { MemberTag } from "./MemberTag";

interface TagsProps {
  comment: Comment;
}

export const Tags = ({ comment }: TagsProps) => {
  const { isNarrow } = useCommentsContainer();

  const tags: ReactNode[] = [];
  if (isAuthorAiAgent(comment)) {
    tags.push(
      <Badge
        key="ai-agent"
        variant="secondary"
        icon="sparkles"
        label={t("comments.author_tags.ai_agent")}
      />,
    );
  }
  if (isAuthorAdmin(comment)) {
    tags.push(
      <Badge
        key="admin"
        label={t("comments.author_tags.admin")}
        {...cssAnchorProps({ anchor: "comment-author-admin-tag" })}
      />,
    );
  }
  filterVisibleTags(comment.member_tags ?? []).forEach(tag => {
    tags.push(<MemberTag key={tag.id} tag={tag} />);
  });

  if (!tags.length) return null;

  const limit = isReply(comment) || isNarrow ? 1 : 2;
  const visibleTags = tags.slice(0, limit);
  const hiddenTags = tags.slice(limit);

  return (
    <div
      className="flex items-center gap-1"
      {...cssAnchorProps({ anchor: "comment-author-tags" })}
    >
      {visibleTags}
      {hiddenTags.length > 0 && (
        <Popover
          trigger={
            <button
              type="button"
              className="inline-flex cursor-pointer rounded-md focus-visible:shadow-focus focus-visible:outline-none"
            >
              <Badge label={`+${hiddenTags.length}`} variant="secondary" />
            </button>
          }
        >
          <div className="flex flex-col gap-1">{hiddenTags}</div>
        </Popover>
      )}
    </div>
  );
};
